import Chainable from "./Chainable.mts";
import Compare from "./Compare.mts";
import Range from "./Range.mts";
import UFunction from "./UFunction.mts";
import UMap from "./UMap.mts";
import UNumber from "./UNumber.mts";
import UObject from "./UObject.mts";
import UTuple from "./UTuple.mts";
import UType from "./UType.mts";

namespace UArray {
  export type Maybe<T> = T | readonly T[];

  export const ensure = <T,>(arr: T) =>
    (Array.isArray(arr) ? arr : [arr]) as T extends readonly any[] ? T : T[];

  export const unwrapFirst = <T,>(arr: T) =>
    (Array.isArray(arr) ? arr[0] : arr) as T extends readonly any[]
      ? T[number]
      : T;

  export type Map<A extends readonly any[], R> = A extends readonly []
    ? []
    : A extends readonly [any, ...infer Rest]
      ? [R, ...Map<Rest, R>]
      : R[];
  export const map = <const A extends readonly any[], R>(
    arr: A,
    mapFn: (
      value: A[number],
      index: Range<0, A["length"]>[number],
      array: A
    ) => R
  ) => arr.map(mapFn as any) as Map<A, R>;

  export type Filter<A extends readonly any[], R> = A extends readonly []
    ? []
    : A extends readonly [infer First, ...infer Rest]
      ? First extends R
        ? [First, ...Filter<Rest, R>]
        : Filter<Rest, R>
      : R[];
  export const filter = <A extends readonly any[], R>(
    arr: A,
    filterFn: (value: A[number], index: number, array: A) => value is R
  ): Filter<A, R> =>
    // @ts-expect-error
    arr.filter(filterFn) as R[];

  export const sliceAfter = Chainable.create(
    UFunction.inferParams(
      ({ including = false }) =>
        <A extends any[]>(
          arr: A,
          predicate: Parameters<A["findIndex"]>[0]
        ): A[number][] => {
          const index = arr.findIndex(predicate);
          if (index === -1) return [];
          return arr.slice(index + (including ? 0 : 1));
        }
    ),
    {
      including: { including: true },
    }
  );

  export const includesLiteral = <const A extends readonly any[], U>(
    array: A,
    searchElement: U,
    fromIndex?: number
  ): searchElement is A[number] & U => array.includes(searchElement, fromIndex);

  export function partition<A extends readonly any[], R>(
    arr: A,
    predicate: (item: A[number], index: number, array: A) => item is R
  ): [Filter<A, R>, Filter<A, Exclude<A[number], R>>];
  export function partition<A extends readonly any[]>(
    arr: A,
    predicate: (item: A[number], index: number, array: A) => unknown
  ): [A[number][], A[number][]];
  export function partition<A extends readonly any[]>(
    arr: A,
    predicate: (item: A[number], index: number, array: A) => unknown
  ): [A[number][], A[number][]] {
    const res: [A[number][], A[number][]] = [[], []];

    for (let i = 0; i < arr.length; ++i) {
      const item = arr[i]!;
      res[predicate(item, i, arr) ? 0 : 1].push(item);
    }

    return res;
  }

  export type CutAt<T extends any[], I extends number> = UTuple.CutAt<T, I>;
  export const cutAt = <A extends any[], I extends number>(arr: A, i: I) =>
    [arr.slice(0, i), arr.slice(i)] as CutAt<A, I>;

  type Chunk<A extends any[], ChunkSize extends number> =
    UTuple.HasPositionalValues<A> extends false
      ? UTuple.Partial<UTuple.Of<A[number], ChunkSize>>[]
      : A extends []
        ? []
        : CutAt<A, ChunkSize> extends [infer Left, infer Right]
          ? // @ts-expect-error
            [Left, ...Chunk<Right, ChunkSize>]
          : never;
  export const chunk = <A extends any[], ChunkSize extends number>(
    arr: A,
    chunkSize: ChunkSize
  ) => {
    const res: A[][] = [];
    for (let i = 0; i < arr.length; ) {
      res.push(arr.slice(i, (i += chunkSize)));
    }
    return res as Chunk<A, ChunkSize>;
  };

  export type WithLengthOf<
    A extends readonly any[],
    Length extends number,
  > = A extends A
    ? UType.Narrow<
        UTuple.CutAt.Strict<A, Length> extends [
          infer Left extends readonly any[],
          any,
        ]
          ? [...Left]
          : never,
        A
      >
    : never;

  export namespace WithLengthOf {
    export type AtLeast<
      A extends readonly any[],
      Length extends number,
    > = UType.Narrow<
      UTuple.CutAt.Strict<A, Length> extends [
        infer Left extends readonly any[],
        infer Right extends readonly any[],
      ]
        ? [...Left, ...Right]
        : never,
      A
    >;
  }

  export type HasLengthOf<
    A extends readonly any[],
    Length extends number,
  > = A extends readonly [...UTuple.Of<any, Length>] ? true : false;

  export namespace HasLengthOf {
    export type AtLeast<
      A extends readonly any[],
      Length extends number,
    > = A extends readonly [...UTuple.Of<any, Length>, ...any[]] ? true : false;
  }

  export function withLengthOf<A extends any[], Length extends number>(
    arr: A,
    length: Length
  ): WithLengthOf<A, Length> | null {
    if (arr.length !== length) return null;
    return arr as WithLengthOf<A, Length>;
  }
  withLengthOf.atLeast = <A extends any[], const Length extends number>(
    arr: A,
    length: Length
  ): WithLengthOf.AtLeast<A, Length> | null => {
    if (arr.length < length) return null;
    return arr as WithLengthOf.AtLeast<A, Length>;
  };

  export function hasLengthOf<
    const A extends readonly any[],
    Length extends number,
  >(arr: A, length: Length): arr is WithLengthOf<A, Length> {
    return arr.length === length;
  }
  hasLengthOf.atLeast = <A extends readonly any[], const Length extends number>(
    arr: A,
    length: Length
  ): arr is WithLengthOf.AtLeast<A, Length> => arr.length >= length;

  export function assertLengthOf<A extends any[], Length extends number>(
    arr: A,
    length: Length,
    message = `Assertion failed: Length of array is not ${length}` as UFunction.Maybe<
      [A],
      string
    >
  ) {
    if (arr.length !== length) throw new Error(UFunction.unwrap(message, arr));
    return arr as WithLengthOf<A, Length>;
  }
  assertLengthOf.atLeast = <A extends any[], const Length extends number>(
    arr: A,
    length: Length,
    message = `Assertion failed: Length of array is less than ${length}` as UFunction.Maybe<
      [A],
      string
    >
  ): WithLengthOf.AtLeast<A, Length> => {
    if (arr.length < length) throw new Error(UFunction.unwrap(message, arr));
    return arr as WithLengthOf.AtLeast<A, Length>;
  };

  export function sum<T>(arr: readonly T[], select: UObject.Select<T, number>) {
    const selector = UObject.createSelector(select);
    let sum = 0;
    for (const item of arr) {
      sum += +selector(item) || 0;
    }
    return sum;
  }

  export const zip = <A1, A2>(arr1: A1[], arr2: A2[]) =>
    [
      ...arr1.flatMap((item, i) =>
        i < arr2.length ? [item, arr2[i]] : [item]
      ),
      ...arr2.slice(arr1.length),
    ] as (A1 | A2)[];

  export const join = <A extends readonly any[], Separator extends string>(
    arr: A,
    separator: Separator
  ) => arr.join(separator) as UTuple.Join<A, Separator>;

  export function joinWith<A extends readonly any[]>(
    arr: A,
    joinFn: (
      index: Range<0, UNumber.Subtract<A["length"], 1>>[number],
      array: A
    ) => string
  ) {
    if (arr.length === 0) return "";
    let res = `${arr[0]}`;
    for (let i = 0; i < arr.length - 1; i++) {
      res += joinFn(i as any, arr) + arr[i + 1];
    }
    return res;
  }

  export const countItems = <T,>(arr: T[]) => {
    const map = new Map<T, number>();

    for (const item of arr) {
      map.set(item, (map.get(item) || 0) + 1);
    }

    return map;
  };

  export function equal(arr1: readonly any[], arr2: readonly any[]) {
    return (
      arr1.length === arr2.length && arr1.every((value, i) => value === arr2[i])
    );
  }
  equal.unordered = (arr1: any[], arr2: any[]) =>
    UMap.equal(countItems(arr1), countItems(arr2));

  export function maxBy<
    const A extends readonly any[],
    Selector extends Compare.Orderable.Selector<A[number]>,
  >(
    arr: A,
    selector: Selector
  ):
    | A[number]
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? Compare.Orderable.Selector.CanReturnNullish<Selector> extends true
          ? undefined
          : never
        : undefined);
  export function maxBy<
    const A extends readonly any[],
    Comparator extends Compare.Comparator<A[number]>,
  >(
    arr: A,
    comparator: Comparator
  ):
    | A[number]
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? UFunction.CanReturnNullish<Comparator> extends true
          ? undefined
          : never
        : undefined);
  export function maxBy<
    const A extends readonly any[],
    SelectorOrComparator extends
      | Compare.Orderable.Selector<A[number]>
      | Compare.Comparator.Nullable<A[number]>,
  >(
    arr: A,
    selectorOrComparator: SelectorOrComparator
  ):
    | A[number]
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? SelectorOrComparator extends Compare.Comparator.Nullable<A[number]>
          ? UFunction.CanReturnNullish<SelectorOrComparator> extends true
            ? undefined
            : never
          : SelectorOrComparator extends Compare.Orderable.Selector<A[number]>
            ? Compare.Orderable.Selector.CanReturnNullish<SelectorOrComparator> extends true
              ? undefined
              : never
            : never
        : undefined);
  export function maxBy<T>(
    arr: readonly T[],
    selectorOrComparator: Compare.Orderable.Selector<T> | Compare.Comparator<T>
  ): T | undefined {
    if (arr.length === 0) return;

    const index = maxIndexBy(arr, selectorOrComparator);

    return index == null ? index : arr[index];
  }

  export function maxIndexBy<
    const A extends readonly any[],
    Selector extends Compare.Orderable.Selector<A[number]>,
  >(
    arr: A,
    selector: Selector
  ):
    | number
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? Compare.Orderable.Selector.CanReturnNullish<Selector> extends true
          ? undefined
          : never
        : undefined);
  export function maxIndexBy<
    const A extends readonly any[],
    Comparator extends Compare.Comparator.Nullable<A[number]>,
  >(
    arr: A,
    comparator: Comparator
  ):
    | number
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? UFunction.CanReturnNullish<Comparator> extends true
          ? undefined
          : never
        : undefined);
  export function maxIndexBy<
    const A extends readonly any[],
    SelectorOrComparator extends
      | Compare.Orderable.Selector<A[number]>
      | Compare.Comparator.Nullable<A[number]>,
  >(
    arr: A,
    selectorOrComparator: SelectorOrComparator
  ):
    | number
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? SelectorOrComparator extends Compare.Comparator.Nullable<A[number]>
          ? UFunction.CanReturnNullish<SelectorOrComparator> extends true
            ? undefined
            : never
          : SelectorOrComparator extends Compare.Orderable.Selector<A[number]>
            ? Compare.Orderable.Selector.CanReturnNullish<SelectorOrComparator> extends true
              ? undefined
              : never
            : never
        : undefined);
  export function maxIndexBy<T>(
    arr: readonly T[],
    selectorOrComparator:
      | Compare.Orderable.Selector<T>
      | Compare.Comparator.Nullable<T>
  ): number | undefined {
    if (!UArray.hasLengthOf.atLeast(arr, 1)) return;

    const comparator = Compare.Comparator.nullable(selectorOrComparator, {
      nulls: "low",
    });

    let maxIndex: number | undefined = 0;
    for (let i = 1; i < arr.length; ++i) {
      const res = comparator(arr[i]!, arr[maxIndex]!);
      if (res == null) continue;
      if (res > 0) maxIndex = i;
    }

    if (maxIndex === 0 && comparator(arr[0], arr[0]) == null)
      maxIndex = undefined;

    return maxIndex;
  }

  export function minBy<
    const A extends readonly any[],
    Selector extends Compare.Orderable.Selector<A[number]>,
  >(
    arr: A,
    selector: Selector
  ):
    | A[number]
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? Compare.Orderable.Selector.CanReturnNullish<Selector> extends true
          ? undefined
          : never
        : undefined);
  export function minBy<
    const A extends readonly any[],
    Comparator extends Compare.Comparator<A[number]>,
  >(
    arr: A,
    comparator: Comparator
  ):
    | A[number]
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? UFunction.CanReturnNullish<Comparator> extends true
          ? undefined
          : never
        : undefined);
  export function minBy<
    const A extends readonly any[],
    SelectorOrComparator extends
      | Compare.Orderable.Selector<A[number]>
      | Compare.Comparator.Nullable<A[number]>,
  >(
    arr: A,
    selectorOrComparator: SelectorOrComparator
  ):
    | A[number]
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? SelectorOrComparator extends Compare.Comparator.Nullable<A[number]>
          ? UFunction.CanReturnNullish<SelectorOrComparator> extends true
            ? undefined
            : never
          : SelectorOrComparator extends Compare.Orderable.Selector<A[number]>
            ? Compare.Orderable.Selector.CanReturnNullish<SelectorOrComparator> extends true
              ? undefined
              : never
            : never
        : undefined);
  export function minBy<T>(
    arr: readonly T[],
    selectorOrComparator:
      | Compare.Orderable.Selector<T>
      | Compare.Comparator.Nullable<T>
  ): T | undefined {
    if (arr.length === 0) return;

    const index = minIndexBy(arr, selectorOrComparator);

    return index == null ? index : arr[index];
  }

  export function minIndexBy<
    const A extends readonly any[],
    Selector extends Compare.Orderable.Selector<A[number]>,
  >(
    arr: A,
    selector: Selector
  ):
    | number
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? Compare.Orderable.Selector.CanReturnNullish<Selector> extends true
          ? undefined
          : never
        : undefined);
  export function minIndexBy<
    const A extends readonly any[],
    Comparator extends Compare.Comparator.Nullable<A[number]>,
  >(
    arr: A,
    comparator: Comparator
  ):
    | number
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? UFunction.CanReturnNullish<Comparator> extends true
          ? undefined
          : never
        : undefined);
  export function minIndexBy<
    const A extends readonly any[],
    SelectorOrComparator extends
      | Compare.Orderable.Selector<A[number]>
      | Compare.Comparator.Nullable<A[number]>,
  >(
    arr: A,
    selectorOrComparator: SelectorOrComparator
  ):
    | number
    | (HasLengthOf.AtLeast<A, 1> extends true
        ? SelectorOrComparator extends Compare.Comparator.Nullable<A[number]>
          ? UFunction.CanReturnNullish<SelectorOrComparator> extends true
            ? undefined
            : never
          : SelectorOrComparator extends Compare.Orderable.Selector<A[number]>
            ? Compare.Orderable.Selector.CanReturnNullish<SelectorOrComparator> extends true
              ? undefined
              : never
            : never
        : undefined);
  export function minIndexBy<T>(
    arr: readonly T[],
    selectorOrComparator:
      | Compare.Orderable.Selector<T>
      | Compare.Comparator.Nullable<T>
  ): number | undefined {
    if (!UArray.hasLengthOf.atLeast(arr, 1)) return;

    const comparator = Compare.Comparator.nullable(selectorOrComparator, {
      nulls: "high",
    });

    let minIndex: number | undefined = 0;
    for (let i = 1; i < arr.length; ++i) {
      const res = comparator(arr[i]!, arr[minIndex]!);
      if (res == null) continue;
      if (res < 0) minIndex = i;
    }

    if (minIndex === 0 && comparator(arr[0], arr[0]) == null)
      minIndex = undefined;

    return minIndex;
  }

  export const dedupe = <T extends any>(...arrs: Maybe<T>[]) =>
    [...new Set(arrs.flat())] as T[];

  export const dedupeBy = <T,>(array: T[], select: UObject.Select<T>) => {
    const selector = UObject.createSelector(select);
    const set = new Set();
    return array.filter((item) => {
      const res = selector(item as any);
      if (set.has(res)) return;
      set.add(res);
      return true;
    });
  };

  export namespace set {
    export function intersection<T>(arr1: readonly T[], arr2: readonly T[]) {
      const set = new Set(arr2);
      return arr1.filter((item) => set.has(item));
    }
    export function union<T>(arr1: readonly T[], arr2: readonly T[]) {
      return dedupe(arr1, arr2);
    }

    export function difference<
      A1 extends readonly any[],
      A2 extends readonly any[],
    >(
      arr1: A1,
      arr2: A2
    ): Exclude<
      A1[number],
      // Can only guarantee that positional items are present in arr2
      // [] is assignable to any array type without positional items
      UTuple.PositionalItems<A2>[number]
    >[] {
      const set = new Set(arr2);
      return arr1.filter((item) => !set.has(item));
    }
    export function symmetricDifference<T>(
      arr1: readonly T[],
      arr2: readonly T[]
    ) {
      return union(difference(arr1, arr2), difference(arr2, arr1));
    }
  }
}

export default UArray;
